import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';
import type { QuoteListRow, QuoteView } from './useQuotes.js';

// Response shapes for the Brief OA oversight endpoints. Declared web-side because
// the API keeps these DTOs feature-local; they mirror
// apps/api/src/features/oversight/oversight.service.ts exactly.
//
// COMMISSION-FREE by construction: OversightQuoteRow is QuoteListRow (already
// commission-free) plus a branch label, and the detail is the same QuoteView the
// consultant screen uses. There is no commission field to render.

/**
 * A cross-branch list row: a quote list row plus the author's branch label. The
 * branch is DERIVED from the author's current office server-side and degrades to
 * null when the author has no branch or was deleted.
 */
export type OversightQuoteRow = QuoteListRow & {
  branchName: string | null;
  branchCode: string | null;
  // Present ONLY on the super_admin cross-agency payload (Brief QUOTES-OVERSIGHT):
  // which agency the quote belongs to. Absent (undefined) for the tenant-scoped
  // owner/accounts payload, which lists a single agency and needs no such column.
  tenantId?: string;
  agencyName?: string | null;
};

export type OversightQuoteListResponse = { items: OversightQuoteRow[] };

const oversightQuotesKey = ['oversight', 'quotes'] as const;
const oversightQuoteKey = (quoteId: string) => [...oversightQuotesKey, quoteId] as const;

/** Every quote in the tenant, cross-branch, newest first. Gated on view_all_branches. */
export function useOversightQuotes() {
  return useQuery({
    queryKey: oversightQuotesKey,
    queryFn: () => apiGet<OversightQuoteListResponse>('/oversight/quotes'),
    staleTime: 30_000,
  });
}

/** One locked quote, read-only. Disabled until an id is known. */
export function useOversightQuote(quoteId: string | null) {
  return useQuery({
    queryKey: quoteId ? oversightQuoteKey(quoteId) : [...oversightQuotesKey, '__none__'],
    queryFn: () => apiGet<{ quote: QuoteView }>(`/oversight/quotes/${quoteId}`),
    enabled: !!quoteId,
  });
}
